import generateObjectId from '../../../utils/generateObjectId';
import { computeLayout } from '../aiLayout';
import { GROUP_KEY_PREFIX, CATEGORY_COLORS } from '../constants';
import { getSpawnOrigin } from './spawnOrigin';
import { resolveOrCreateVariable } from './variables';
import { wrapStepError, describeError } from './errors';
import { createStepBlocks } from './stepBlocks';
import { createStepLinks } from './stepLinks';

export const createScenarioEngine = (env) => {
    const {
        addBlock, createPath, addVariable, updateBlockData, updateBlockDataJump, createCategory, deleteCategory, deleteBlock,
        actionGetBotInformations, refreshDeeplyConversation, panzone, spawnPosition, variables, botName, canvasBlocksRef, canvasCategoriesRef,
    } = env;
    const { createStepBlock, createGroupHeader } = createStepBlocks({ addBlock, addVariable, updateBlockData });
    const { connectStepPaths } = createStepLinks({ createPath, updateBlockData, updateBlockDataJump });

    const runSteps = async (steps, knownVariables, stepMap = {}, groups = [], categoryList = []) => {
        const { x, y } = getSpawnOrigin(panzone, spawnPosition);
        const layout = computeLayout(steps, groups);
        const groupsById = Object.fromEntries(groups.map((group) => [group.id, group]));

        for (const group of groups) {
            try {
                await createGroupHeader(group, layout.headers[group.id], x, y, stepMap);
            } catch (err) {
                throw new Error('group "' + group.id + '" (creating its scenario block): ' + describeError(err));
            }
        }

        for (const step of steps) {
            try {
                await createStepBlock(step, layout.positions[step.id], x, y, knownVariables, stepMap);
            } catch (err) {
                throw wrapStepError(err, step, 'creating the block');
            }
        }

        for (const group of groups) {
            const header = stepMap[GROUP_KEY_PREFIX + group.id];
            const first = steps.find((step) => step.group === group.id);
            if (header && first && stepMap[first.id]) {
                try {
                    await createPath(stepMap[first.id].objectId, generateObjectId(), header.children[0].object_id);
                } catch (err) {
                    throw new Error('group "' + group.id + '" (linking its scenario block to the first step): ' + describeError(err));
                }
            }
        }

        for (const step of steps) {
            try {
                await connectStepPaths(step, stepMap, groupsById);
            } catch (err) {
                throw wrapStepError(err, step, 'linking the block');
            }
        }

        for (const group of groups) {
            const members = [GROUP_KEY_PREFIX + group.id, ...steps.filter((step) => step.group === group.id).map((step) => step.id)]
                .map((key) => stepMap[key]?.objectId)
                .filter(Boolean);
            if (members.length === 0) continue;

            const objectId = generateObjectId();
            const created = await createCategory({ color: CATEGORY_COLORS[group.color] || CATEGORY_COLORS.blue, name: group.name, object_id: objectId, blocks_object_id: members });
            if (created) categoryList.push({ objectId, name: group.name });
        }

        // The camera is deliberately not moved here
        return Object.values(stepMap).map(entry => entry.objectId);
    };

    const applyScenario = async (steps, referencedVariables, stepMap, groups = [], categoryList = []) => {
        const knownVariables = [...variables];
        const newlyCreatedNames = [];
        // A purely numeric name is an array index, not a variable
        const variableNames = (referencedVariables || []).map(String).filter((name) => !/^\d+$/.test(name));
        for (const name of variableNames) {
            const existedBefore = knownVariables.some(v => v.name.toLowerCase() === name.toLowerCase());
            await resolveOrCreateVariable(name, 'text', knownVariables, addVariable);
            if (!existedBefore) newlyCreatedNames.push(name);
        }

        await runSteps(steps, knownVariables, stepMap, groups, categoryList);
        await actionGetBotInformations(botName, false);
        // Deep refresh of the test chatbox, not done on every Test click (Menu.js)
        refreshDeeplyConversation({ followBlock: false });

        return newlyCreatedNames;
    };

    const removeCategoriesFromCanvas = async (categoryList = []) => {
        for (const item of categoryList) {
            const found = (canvasCategoriesRef.current || []).find((category) => category.object_id === item.objectId || category.category?.object_id === item.objectId);
            const categoryId = found?.category?.id;
            if (categoryId !== undefined && categoryId !== null) {
                await deleteCategory(categoryId, { id: categoryId });
            }
        }
    };

    const removeBlocksFromCanvas = async (blockIds) => {
        const present = new Set((canvasBlocksRef.current || []).map((block) => block.object_id));
        const toDelete = blockIds.filter((id) => present.has(id));

        for (const blockId of toDelete) {
            await deleteBlock(blockId);
        }
        if (toDelete.length > 0) {
            await actionGetBotInformations(botName, false);
            refreshDeeplyConversation({ followBlock: false });
        }

        return toDelete.length;
    };

    return { runSteps, applyScenario, removeCategoriesFromCanvas, removeBlocksFromCanvas };
};
